import { useState, useEffect, useCallback, useMemo } from "react";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import { formatDate } from "../../utils/date";
import styles from "./Products.module.scss";
import Button from "../../components/common/button/Button";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import { useNavigate } from "react-router-dom";
import ProductsService from "../../api/services/products.service";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import { ProductsTypes } from "../../api/types/products.types";
import Loading from "../../components/common/loading/Loading";
import { toast } from "sonner";
import ShowImage from "../../components/common/show-image/ShowImage";
import UnsavedProducts from "./unsaved-products/UnsavedProducts";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const Products = () => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteProductId, setDeleteProductId] = useState<number | null>(null);
  const [deleteAllVariants, setDeleteAllVariants] = useState(false);
  const [deleteProductName, setDeleteProductName] = useState<string>("");
  const navigate = useNavigate();
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(
    new Set(),
  );
  const { t } = useTranslation("translation");
  const getProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ProductsService.getAllProductVariants(page, limit);
      setProducts(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleDelete = async (id: number, product_id: number) => {
    try {
      if (deleteAllVariants) {
        const variantsToDelete = products.filter(
          (p) => p.product_id === product_id,
        );
        await Promise.all(
          variantsToDelete.map((variant) =>
            ProductsService.deleteProductVariant(
              variant.id,
              variant.product_id,
            ),
          ),
        );
      } else {
        await ProductsService.deleteProductVariant(id, product_id);
      }
      setDeleteModal(false);
      setDeleteAllVariants(false);
      getProducts();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    }
  };

  const groupedProducts = useMemo(() => {
    return products.reduce(
      (acc, variant) => {
        const productId = variant.product_id;
        if (!acc[productId]) {
          acc[productId] = [];
        }
        acc[productId].push(variant);
        return acc;
      },
      {} as Record<number, ProductsTypes[]>,
    );
  }, [products]);

  const displayData = useMemo(() => {
    return Object.values(groupedProducts).map((variants) => {
      const mainVariant = variants[0];
      return {
        ...mainVariant,
        _variants: variants,
        _hasMultiple: variants.length > 1,
      };
    });
  }, [groupedProducts]);

  const toggleProductGroup = (productId: number) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const getLocalizedName = (row: any, lang: string): string => {
    const priorities: Record<string, (keyof typeof row)[]> = {
      az: ["name_az", "name_en", "name_de", "name_es"],
      en: ["name_en", "name_az", "name_de", "name_es"],
      es: ["name_es", "name_en", "name_az", "name_de"],
      de: ["name_de", "name_en", "name_az", "name_es"],
    };

    const order = priorities[lang] || priorities["en"];

    for (const key of order) {
      const value = row[key];
      if (value && typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return "Unnamed Variant";
  };

  const columns: Column[] = [
    { key: "sku", header: t("columns.sku") || "Sku" },
    {
      key: "name",
      header: t("columns.name") || "Name",
      render: (_, row) => {
        const productName = getLocalizedName(row, i18n.language);
        return <p className={styles.content}>{productName}</p>;
      },
    },
    {
      key: "category",
      header: t("columns.category") || "Category",
      render: (_, row) => {
        const category = row.product.category;
        const lang = i18n.language;

        let categoryName: string | undefined;
        if (category) {
          switch (lang) {
            case "en":
              categoryName = category.name_en;
              break;
            case "az":
              categoryName = category.name_az;
              break;
            case "de":
              categoryName = category.name_de;
              break;
            case "es":
              categoryName = category.name_es;
              break;
            default:
              categoryName = category.name;
          }
          if (!categoryName) {
            categoryName = category.name;
          }
        }

        return categoryName || "-";
      },
    },
    {
      key: "brand",
      header: t("columns.brand") || "Brand",
      render: (_, row) => row.product.brand.name || "-",
    },
    {
      key: "price",
      header: t("columns.price") || "Price",
      render: (p, row) => (
        <p className={styles.price_content}>
          {`${p} ${row?.product?.currency?.name}` || "-"}
        </p>
      ),
    },
    {
      key: "discount_percent",
      header: t("labels.discount_percent") || "Discount Percent",
      render: (p) => <p className={styles.content}>{`${p}%` || "-"}</p>,
    },
    {
      key: "discount_price",
      header: t("labels.discounted_price") || "Discount Price",
      render: (p, row) => (
        <p className={styles.content}>
          {`${p} ${row?.product?.currency?.name}` || "-"}
        </p>
      ),
    },
    {
      key: "stock_quantity",
      header: t("columns.stock") || "Stock",
    },
    {
      key: "images",
      header: t("columns.images") || "Images",
      render: (imgs) => {
        return imgs?.slice(0, 2)?.map((img: any, index: number) => {
          return img.image ? (
            <div
              key={index}
              style={{
                cursor: "pointer",
                display: "inline-block",
                marginRight: "5px",
              }}
              onClick={() => {
                setSelectedImages(imgs);
                setShowImageModal(true);
              }}
            >
              <img
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
                src={img.image}
                alt={"products"}
              />
            </div>
          ) : null;
        });
      },
    },
    {
      key: "created",
      header: t("columns.date") || "Date",
      render: (v) => formatDate(v, true),
    },
  ];

  if (loading) return <Loading />;

  return (
    <>
      <div className={styles.products}>
        <TitleUpdater title={t("products")} />
        <div className={styles.section_header}>
          <h2 className={styles.title}>{t("products")}</h2>
          <div className={styles.actions}>
            <Button onClick={() => navigate("/dashboard/products/create")} icon>
              {t("buttons.create")}
            </Button>
          </div>
        </div>
        <DataTable
          data={displayData}
          columns={columns}
          actions
          onDelete={(id, hasMultipleVariants) => {
            const variant = displayData
              .flatMap((item: any) => item._variants || [item])
              .find((v: any) => v.id === id);
            setDeleteModal(true);
            setDeleteId(id);
            setDeleteProductId(variant?.product_id || null);
            setDeleteAllVariants(hasMultipleVariants || false);
            setDeleteProductName(getLocalizedName(variant, i18n.language));
          }}
          onEdit={(id) => navigate(`/dashboard/products/edit/${id}`)}
          paginationData={{
            total: count,
            page,
            limit,
            onPageChange: handlePageChange,
            onLimitChange: handleLimitChange,
          }}
          isProductVariantTable={true}
          expandedProducts={expandedProducts}
          onToggleExpand={toggleProductGroup}
        />

        <DeleteModal
          deleteModal={deleteModal}
          onClose={() => {
            setDeleteModal(false);
            setDeleteAllVariants(false);
            setDeleteProductName("");
          }}
          onSubmit={() =>
            handleDelete(Number(deleteId), Number(deleteProductId))
          }
          title={
            deleteAllVariants
              ? (t("delete_all_variants_confirm", {
                  productName: deleteProductName,
                }) ?? "Delete all variants?")
              : (t("delete_single_variant_confirm", {
                  productName: deleteProductName,
                }) ?? "Delete variant?")
          }
        />
        <ShowImage
          imagesModal={showImageModal}
          setImagesModal={setShowImageModal}
          images={selectedImages}
        />
      </div>
      {!loading && <UnsavedProducts />}
    </>
  );
};

export default Products;
